import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, BookText, Languages, Drama, GraduationCap, Award, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

export const Programs = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const programs = [
    {
      id: 'french',
      icon: Flag,
      flag: '🇫🇷',
      features: ['Brevet des Collèges', 'Baccalauréat'],
      color: 'bg-blue-600',
      accent: 'text-blue-600',
      size: 'large',
      hasVisitBtn: true,
    },
    {
      id: 'english',
      icon: BookText,
      flag: '🇬🇧',
      features: ['Cambridge Certifications', 'Native Speakers'],
      color: 'bg-red-600',
      accent: 'text-red-600',
      size: 'medium',
      hasVisitBtn: true,
    },
    {
      id: 'spanish',
      icon: Languages,
      flag: '🇪🇸',
      features: ['DELE Certification', 'Cultural Immersion'],
      color: 'bg-amber-600',
      accent: 'text-amber-600',
      size: 'small',
    },
    {
      id: 'chinese',
      icon: Languages,
      flag: '🇨🇳',
      features: ['HSK Certification', '汉语水平考试', 'Mandarin'],
      color: 'bg-red-700',
      accent: 'text-red-700',
      size: 'small',
    },
    {
      id: 'theatre',
      icon: Drama,
      flag: '🎭',
      features: ['New Era Academy', 'Public Speaking', 'Stage Performance'],
      color: 'bg-purple-600',
      accent: 'text-purple-600',
      size: 'small',
    },
    {
      id: 'cambridgeCenter',
      icon: GraduationCap,
      flag: '🏛️',
      features: ['Cambridge YLE', 'KET / PET', 'FCE / CAE'],
      color: 'bg-blue-700',
      accent: 'text-blue-700',
      size: 'small',
    },
  ];

  const certifications = [
    { name: 'Cambridge University', logo: '🏛️' },
    { name: 'Instituto Cervantes', logo: '📜' },
    { name: 'French Ministry', logo: '🎓' },
    { name: 'New Era Academy', logo: '🎭' },
  ];

  return (
    <section id="programs" data-testid="programs-section" ref={ref} className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('programs.tag')}
          </span>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6">
            {t('programs.title1')} <span className="italic">{t('programs.title1Italic')}</span>, {t('programs.title2')} <span className="italic">{t('programs.title2Italic')}</span>
          </h2>
          <div className="section-divider mb-6" />
          <p className="font-inter text-slate-600 text-lg max-w-2xl mx-auto">
            {t('programs.subtitle')}
          </p>
        </motion.div>

        <div className="bento-grid">
          {programs.map((program, index) => (
            <motion.div
              key={program.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              data-testid={`program-card-${program.id}`}
              className={`bg-white rounded-2xl border border-slate-200 overflow-hidden card-hover bento-item-${program.size}`}
            >
              <div className="p-8 h-full flex flex-col">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-14 h-14 rounded-xl ${program.color} flex items-center justify-center`}>
                    <program.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-4xl">{program.flag}</span>
                </div>
                <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-1">
                  {t(`programs.${program.id}.title`)}
                </h3>
                <span className={`text-sm font-semibold ${program.accent} mb-4`}>
                  {t(`programs.${program.id}.subtitle`)}
                </span>
                <p className="font-inter text-slate-600 leading-relaxed mb-6 flex-grow">
                  {t(`programs.${program.id}.description`)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {program.features.map((feature) => (
                    <span key={feature} className="bg-slate-100 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
                {program.hasVisitBtn && (
                  <Button
                    data-testid={`program-visit-btn-${program.id}`}
                    onClick={() => {
                      const el = document.querySelector('#contact');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-2.5 w-fit text-sm font-semibold group"
                  >
                    <span>{t('admissions.requestVisit')}</span>
                    <ArrowRight className="w-4 h-4 ml-2 inline-block group-hover:translate-x-1 transition-transform" />
                  </Button>
                )}
              </div>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-testid="exam-center-card"
            className="bento-item-large bg-slate-900 rounded-2xl overflow-hidden"
          >
            <div className="p-8 h-full flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center mb-6">
                  <Award className="w-7 h-7 text-slate-900" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-white mb-2">
                  {t('programs.examCenter.title')}
                </h3>
                <p className="font-inter text-slate-400 leading-relaxed mb-6">
                  {t('programs.examCenter.description')}
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                {certifications.map((cert) => (
                  <div key={cert.name} className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-full">
                    <span className="text-xl">{cert.logo}</span>
                    <span className="text-white text-sm font-medium">{cert.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bento-item-medium bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl overflow-hidden"
          >
            <div className="p-8 h-full flex flex-col justify-between">
              <div>
                <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-2">
                  {t('programs.cta.title')}
                </h3>
                <p className="font-inter text-slate-800 leading-relaxed">
                  {t('programs.cta.description')}
                </p>
              </div>
              <Button
                data-testid="programs-cta-btn"
                onClick={() => {
                  const el = document.querySelector('#contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-3 w-fit mt-6 group"
              >
                <span>{t('programs.cta.button')}</span>
                <ArrowRight className="w-4 h-4 ml-2 inline-block group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
